import React, { useState, useEffect, useCallback, useRef } from "react";
import TextField from "@mui/material/TextField";
import CustomButton from "../../../../controls/CustomButton";
import useTitanApp from "../../../../../hooks/useTitanApp";
import {
  saveAiTemplate,
  setOpenAddTemplate,
  setAiPatientTemplate,
  updateAiTemplateToggle,
  setTemplateStructure,
  addConsultationTemplate,
} from "../../../../../dispatchers/patientDispatchers";
import "./../../../../../assets/css/PatientConsultation.scss";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { GoPlusCircle } from "react-icons/go";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material/styles";
import { FaEdit } from "react-icons/fa";
import CloseIcon from "@mui/icons-material/Close";
import { MdOutlineDelete } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import AiStreamResponse from "../../../../../utils/AiStreamResponse";
import { generateAiServiceTemplateUrl } from "../../../../../apiUrls/apiUrls";
import {
  getAiTemplateByCompanyId,
  getAiTemplateById,
  setHydratedTemplateSections,
  setIsTemplateSelected,
  setSelectedTemplate,
} from "../../../../../dispatchers/templateDispatchers";
import Skeleton from "@mui/material/Skeleton";
import AutocueSectionSkeleton from "./AutocueSkeleton";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import AddPatientTemplateSection from "./AddPatientTemplateSection";

function AddPatientTemplate({ promptData, attachment, handleDialogClose }) {
  const { appState, patientState } = useTitanApp();
  const { darkMode, companyId } = appState || {};
  const {
    aiTemplate,
    aiTemplateLoader,
    patientTranscription,
    templateStructure,
    consultationId,
  } = patientState || {};
  const [templateName, setTemplateName] = useState("");
  const [editableSummary, setEditableSummary] = useState("");
  const [sectionsData, setSectionsData] = useState([]);
  const [buttonsDisabled, setButtonsDisabled] = useState(true);

  const [errors, setErrors] = useState({
    templateName: false,
    templateStructure: false,
    sections: [],
  });

  const [isDefault, setIsDefault] = useState(false);
  const [makeVisibleToOthers, setMakeVisibleToOthers] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  const [isGenerating, setIsGenerating] = useState(false);
  const [streamedText, setStreamedText] = useState("");
  const accumulatedText = useRef("");
  const typingRef = useRef(null);
  const [templateDropdown, setTemplateDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [aiqa, setAiqa] = useState([]);

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: "#1976d2",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(["width"], {
        duration: 200,
      }),
    },
    "& .MuiSwitch-track": {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: "#444",
      boxSizing: "border-box",
    },
  }));

  const removeBracketsAndParentheses = (text) => {
    if (!text) return text;
    return text.replace(/\[|\]/g, "").replace(/\(.*?\)/g, "");
  };

  useEffect(() => {
    if (aiTemplate?.templateSections) {
      const mappedSections = aiTemplate.templateSections.map((section) => ({
        groupId: section.groupId,
        sectionTitle: section.sectionTitle,
        userControls: section.userControls.map((control, index) => ({
          id: `control-${section.groupId}-${index}`,
          label: control.label,
          required: control.required,
          value: control.value ?? null,
        })),
      }));
      setSectionsData(mappedSections);
    }
    setTemplateName(aiTemplate?.templateName || "");
    setEditableSummary(aiTemplate?.consultationSummary || "");
    setEditableSummary(templateStructure);
  }, [aiTemplate]);

   useEffect(() => {
    if (aiTemplateLoader) {
      setButtonsDisabled(true);
      return;
    }
    setButtonsDisabled(false);
  }, [aiTemplateLoader]);

  useEffect(() => {
    const loadTemplateData = async () => {
      if (promptData || attachment) {
        const requestData = {
          prompt: promptData,
          attachment: attachment || "",
        };
        try {
          setIsGenerating(true);
          setStreamedText("");
          accumulatedText.current = "";

          await AiStreamResponse(
            requestData,
            (chunk) => {
              accumulatedText.current += chunk;
              setStreamedText(
                removeBracketsAndParentheses(accumulatedText.current)
              );
              setTemplateStructure(
                removeBracketsAndParentheses(accumulatedText.current)
              );
            },
            () => {
              setIsGenerating(false);
              try {
                const apiData = JSON.parse(accumulatedText.current);
                if (apiData) {
                  mapApiDataToTemplate(apiData);
                }
              } catch (error) {
                console.error("Error parsing API response:", error);
                setEditableSummary(
                  removeBracketsAndParentheses(accumulatedText.current)
                );
              }
            },
            generateAiServiceTemplateUrl(requestData)
          );
        } catch (error) {
          console.error("Error loading template data:", error);
          setIsGenerating(false);
        }
      }
    };

    loadTemplateData();

    return () => {
      if (typingRef.current) {
        clearInterval(typingRef.current);
      }
    };
  }, [promptData, attachment]);

  const mapApiDataToTemplate = (apiData) => {
    try {
      if (apiData?.templateName) {
        setTemplateName(apiData?.templateName);
      }

      if (apiData?.consultationSummary) {
        setEditableSummary(
          removeBracketsAndParentheses(apiData?.consultationSummary)
        );
      }

      if (
        apiData.templateSections &&
        Array.isArray(apiData?.templateSections)
      ) {
        const mappedSections = apiData?.templateSections?.map((section) => ({
          groupId: section.groupId || `group-${uuidv4()}`,
          sectionTitle: removeBracketsAndParentheses(
            section.sectionTitle || "New Section"
          ),
          userControls: section?.userControls?.map((control, index) => ({
            id: `control-${section.groupId || uuidv4()}-${index}`,
            label: removeBracketsAndParentheses(control.label || ""),
            required: control.required !== undefined ? control.required : true,
            value: control.value || null,
          })) || [
            {
              id: `control-${section.groupId || uuidv4()}-0`,
              label: "",
              required: true,
              value: null,
            },
          ],
        }));
        setSectionsData(mappedSections);
      }
    } catch (error) {
      console.error("Error mapping API data to template:", error);
    }
  };

  const handleControlLabelChange = (sectionIndex, controlIndex, newLabel) => {
    setSectionsData((prev) => {
      const updated = [...prev];
      updated[sectionIndex].userControls[controlIndex].label = newLabel;
      return updated;
    });
  };

  const handleSectionDragEnd = (result) => {
    const { destination, source, type } = result;
    if (!destination) return;
    if (destination.index === source.index) return;
    if (type !== "section") return;

    setSectionsData((prevSections) => {
      const newSections = Array.from(prevSections);
      const [movedSection] = newSections.splice(source.index, 1);
      newSections.splice(destination.index, 0, movedSection);
      return newSections;
    });
    setErrors((prevErrors) => {
      const newSectionErrors = Array.from(prevErrors.sections);
      const [movedError] = newSectionErrors.splice(source.index, 1);
      newSectionErrors.splice(destination.index, 0, movedError);
      return {
        ...prevErrors,
        sections: newSectionErrors,
      };
    });
  };

  const handleQuestionDragEnd = (result) => {
    const { destination, source, type } = result;
    if (!destination) return;
    if (destination.index === source.index) return;
    if (!type.startsWith("question-")) return;
    const sectionIndex = parseInt(type.split("-")[1]);

    setSectionsData((prevSections) => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      const newUserControls = Array.from(section.userControls);

      const [movedQuestion] = newUserControls.splice(source.index, 1);
      newUserControls.splice(destination.index, 0, movedQuestion);

      section.userControls = newUserControls;
      newSections[sectionIndex] = section;
      return newSections;
    });

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      const newSectionErrors = [...newErrors.sections];

      if (newSectionErrors[sectionIndex]?.userControls) {
        const newQuestionErrors = Array.from(
          newSectionErrors[sectionIndex].userControls
        );
        const [movedError] = newQuestionErrors.splice(source.index, 1);
        newQuestionErrors.splice(destination.index, 0, movedError);

        newSectionErrors[sectionIndex] = {
          ...newSectionErrors[sectionIndex],
          userControls: newQuestionErrors,
        };
      }

      return {
        ...newErrors,
        sections: newSectionErrors,
      };
    });
  };

  const handleDragEnd = (result) => {
    const { type } = result;

    if (type === "section") {
      handleSectionDragEnd(result);
    } else if (type.startsWith("question-")) {
      handleQuestionDragEnd(result);
    }
  };

  const handleSave = useCallback(async () => {
    setButtonsDisabled(true);
    let hasError = false;

    if (!templateName.trim()) {
      hasError = true;
      setErrors((prev) => ({ ...prev, templateName: true }));
    } else {
      setErrors((prev) => ({ ...prev, templateName: false }));
    }

    if (!editableSummary.trim()) {
      hasError = true;
      setErrors((prev) => ({ ...prev, templateStructure: true }));
    } else {
      setErrors((prev) => ({ ...prev, templateStructure: false }));
    }

    const sectionErrors = sectionsData.map((section) => {
      const sectionError = {
        sectionTitle: !section.sectionTitle.trim(),
        userControls: section.userControls.map(
          (control) => !control.label.trim()
        ),
      };
      if (
        sectionError.sectionTitle ||
        sectionError.userControls.includes(true)
      ) {
        hasError = true;
      }
      return sectionError;
    });

    setErrors((prev) => ({ ...prev, sections: sectionErrors }));

    if (hasError) {
      console.error("Validation failed: Please fill all required fields.");
      return;
    }

    const finalData = {
      ...aiTemplate,
      companyID: makeVisibleToOthers ? null : companyId,
      templateName: templateName,
      consultationSummary: editableSummary,
      isDefault,
      templateSections: sectionsData.map((section) => ({
        sectionTitle: section.sectionTitle,
        userControls: section.userControls.map((control) => ({
          label: control.label,
          required: control.required,
          value: control.value,
        })),
      })),
    };

    try {
      await saveAiTemplate(finalData);
      setOpenAddTemplate(false);
      setAiPatientTemplate([]);
      handleDialogClose();

      var allTemplates = await getAiTemplateByCompanyId(companyId);
      var lastTemplate = allTemplates[allTemplates?.length - 1];
      addConsultationTemplate({
        consultationId: consultationId,
        template: lastTemplate,
      });
      setTemplateDropdown(false);
      setIsTemplateSelected(true);
      setSelectedTemplate(lastTemplate);
      setHydratedTemplateSections(null);
      setSearchTerm("");
      setAiqa([]);

      await getAiTemplateById(lastTemplate?._id);
    } catch (error) {
      console.error("Failed to save template:", error);
    }
  }, [
    aiTemplate,
    templateName,
    editableSummary,
    sectionsData,
    companyId,
    isDefault,
    makeVisibleToOthers,
  ]);

  const handleDialogCancel = useCallback(() => {
    setOpenAddTemplate(false);
    setAiPatientTemplate([]);
    handleDialogClose();
  }, []);

  const handleSectionTitleChange = (sectionIndex, newTitle) => {
    setSectionsData((prev) => {
      const updated = [...prev];
      updated[sectionIndex].sectionTitle = newTitle;
      return updated;
    });
  };

  const handleAddUserControl = (sectionIndex) => {
    setSectionsData((prev) => {
      const updated = [...prev];
      const newControlId = `control-${updated[sectionIndex].groupId}-${updated[sectionIndex].userControls.length}`;
      updated[sectionIndex].userControls.push({
        id: newControlId,
        label: "",
        value: null,
        required: true,
      });
      return updated;
    });

    setErrors((prev) => {
      const updatedSections = [...prev.sections];
      if (!updatedSections[sectionIndex]) {
        updatedSections[sectionIndex] = {
          sectionTitle: false,
          userControls: [],
        };
      }
      updatedSections[sectionIndex].userControls.push(false);
      return { ...prev, sections: updatedSections };
    });
  };

  const handleDeleteUserControl = (sectionIndex, controlIndex) => {
    setSectionsData((prev) => {
      const updated = [...prev];
      if (updated[sectionIndex].userControls.length === 1) {
        return prev;
      }
      updated[sectionIndex].userControls.splice(controlIndex, 1);
      return updated;
    });

    setErrors((prev) => {
      const updatedSections = [...prev.sections];
      if (updatedSections[sectionIndex]?.userControls) {
        if (updatedSections[sectionIndex].userControls.length === 1) {
          return prev;
        }
        updatedSections[sectionIndex].userControls.splice(controlIndex, 1);
      }
      return { ...prev, sections: updatedSections };
    });
  };

  const handleDeleteSection = (sectionIndex) => {
    setSectionsData((prev) => {
      const updated = [...prev];
      updated.splice(sectionIndex, 1);
      return updated;
    });

    setErrors((prev) => {
      const updatedSections = [...prev.sections];
      updatedSections.splice(sectionIndex, 1);
      return { ...prev, sections: updatedSections };
    });
  };

  const handleAddSection = () => {
    const newGroupId = `group-${uuidv4()}`;
    setSectionsData((prev) => [
      ...prev,
      {
        sectionTitle: "New Section",
        groupId: newGroupId,
        userControls: [
          {
            id: `control-${newGroupId}-0`,
            label: "",
            required: true,
            value: null,
          },
        ],
      },
    ]);

    setErrors((prev) => {
      const updatedSections = [...prev.sections];
      updatedSections.push({
        sectionTitle: false,
        userControls: [false],
      });
      return { ...prev, sections: updatedSections };
    });
  };

  return (
    <div className="consultation_template_wrapper">
      <div className="consultation_content_wrapper">
        <div className="template_content_sections">
          {isGenerating ? (
            <Skeleton
              sx={{
                bgcolor: darkMode ? "grey.800" : "",
                borderRadius: "16px",
              }}
              height={40}
              animation="wave"
              variant="rounded"
            />
          ) : (
            <div className="template_name_field">
              <TextField
                label="Template Name"
                fullWidth
                required
                error={errors.templateName}
                helperText={
                  errors.templateName ? "Template Name is required" : ""
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    backgroundColor: darkMode ? "#111" : "#fff",
                    fontSize: "14px",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "14px",
                  },
                }}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>
          )}

          <TextField
            label="Template Structure"
            multiline
            variant="outlined"
            minRows={25}
            maxRows={25}
            value={isGenerating ? streamedText : editableSummary}
            error={errors.templateStructure}
            helperText={
              errors.templateStructure ? "Template Structure is required" : ""
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                backgroundColor: darkMode ? "#111" : "#fff",
                fontSize: "14px",
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "14px",
              },
            }}
            fullWidth
            required
            onChange={(e) => setEditableSummary(e.target.value)}
          />
        </div>
      </div>
      <div className="template_additional_wrapper">
        <div className="autocue_questions_wrapper">
          <div className="autocue_questions_container">
            <div className="autocue_heading_section">
              <p>Autocue Questions</p>
              <div className="ai_generate_tag">
                <div
                  className="ai-generate-button"
                  disabled={isGenerating || !patientTranscription}
                >
                  <span className="sparkle-icon">✨</span>
                  {isGenerating ? "Generating..." : "AI Generate"}
                </div>
                <AiOutlineExclamationCircle fill="#999999" size={20} />
              </div>
            </div>

            {aiTemplateLoader ? (
              <AutocueSectionSkeleton />
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable
                  droppableId="sections"
                  type="section"
                  className="template_sections_wrapper"
                >
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {sectionsData.map((section, sectionIndex) => (
                        <Draggable
                          key={section.groupId}
                          draggableId={section.groupId}
                          index={sectionIndex}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <AddPatientTemplateSection
                                section={section}
                                sectionIndex={sectionIndex}
                                errors={errors}
                                onSectionTitleChange={handleSectionTitleChange}
                                onControlLabelChange={handleControlLabelChange}
                                onAddUserControl={handleAddUserControl}
                                onDeleteUserControl={handleDeleteUserControl}
                                onDeleteSection={handleDeleteSection}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            <div className="add_template_button_wrapper">
              {isGenerating ? (
                <Skeleton
                  sx={{
                    bgcolor: darkMode ? "grey.800" : "",
                    borderRadius: "16px",
                  }}
                  height={40}
                  animation="wave"
                  variant="rounded"
                />
              ) : (
                <div className="add_template_btn" onClick={handleAddSection}>
                  <p>Add Another</p>
                  <GoPlusCircle
                    fill="#999999"
                    size={20}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="additional_action_btns">
          {isGenerating ? (
            [...Array(2)].map((_, index) => (
              <Skeleton
                key={index} // ✅ Added key to avoid React warning
                sx={{
                  bgcolor: darkMode ? "grey.800" : "",
                  marginBottom: "8px",
                }}
                height={40}
                animation="wave"
                variant="rounded"
              />
            ))
          ) : (
            <>
              <CustomButton
                action={handleDialogCancel}
                className={"additional_cancel_btn"}
                label={"Cancel"}
              />
              <CustomButton
                className={"additional_save_btn"}
                label={"Save Template"}
                action={handleSave}
                isdisable={buttonsDisabled}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AddPatientTemplate;
